import { useState } from 'react';
import { PlayCircleOutlined } from '@ant-design/icons';

import { Badge, Button, Dialog, Field, Select, Skeleton } from '../../../../components/ui';
import { useLoi } from '../../../../hooks/useLoi';
import { useT } from '../../../../i18n';
import { ngayGioVN } from '../../../../lib/dinh-dang';
import { useToast } from '../../../../lib/toast';
import { LoiTrongHop } from '../../../tenant/components/phan-form';
import { LoiRpa } from '../chung';
import { BadgeTrangThaiLuot, TruongThongTin } from '../lich-su/nhan';
import { LOAI_HANH_DONG } from '../../constants';
import { useThucThiHanhDong, useXemTruocHanhDong } from '../../hooks/useAgent';
import { projectChonSan } from '../../nghiep-vu/agent';
import type { HanhDongAgent } from '../../types';

/**
 * Xác nhận chạy một hành động agent đề xuất (`AgentActionExecuteDialog` của native): `run_workflow`, `retry_run`. Máy
 * chủ tự đối chiếu đề xuất với OpenCore (agent sinh `target`, không đáng tin); người dùng chỉ chọn Project có License
 * còn hiệu lực rồi xác nhận. Robot chạy thật ngay sau khi xác nhận.
 */
export function HopThucThi({
  taskId,
  index,
  hanhDong,
  onClose,
}: {
  taskId: string;
  index: number;
  hanhDong: HanhDongAgent;
  onClose: () => void;
}) {
  const { t, locale } = useT();
  const toast = useToast();
  const dichLoi = useLoi();
  const xem = useXemTruocHanhDong(taskId, index);
  const thucThi = useThucThiHanhDong();
  const [daChon, setDaChon] = useState<string>();
  const chayLai = hanhDong.type === 'retry_run';
  const projects = xem.data?.projects ?? [];
  const projectId = projectChonSan(projects, daChon);
  const loai = LOAI_HANH_DONG[hanhDong.type];

  const xacNhan = () => {
    if (!projectId || thucThi.isPending) return;
    thucThi.mutate(
      { taskId, index, projectId },
      {
        onSuccess: () => {
          toast.success(t(chayLai ? 'rpaAgent.thucThi.daGuiChayLai' : 'rpaAgent.thucThi.daGuiChay'));
          onClose();
        },
      },
    );
  };

  return (
    <Dialog
      open
      onClose={onClose}
      width={560}
      destroyOnHidden
      title={t(chayLai ? 'rpaAgent.thucThi.tieuDeChayLai' : 'rpaAgent.thucThi.tieuDeChay')}
      footer={
        <div className="space-y-3">
          {thucThi.error && <LoiTrongHop>{dichLoi(thucThi.error)}</LoiTrongHop>}
          <div className="flex flex-wrap justify-end gap-3">
            <Button variant="outline" size="md" onClick={onClose}>
              {t('chung.huy')}
            </Button>
            <Button
              variant="primary"
              size="md"
              icon={<PlayCircleOutlined />}
              loading={thucThi.isPending}
              disabled={!projectId || !xem.isSuccess}
              onClick={xacNhan}
            >
              {thucThi.isPending
                ? t('rpaAgent.thucThi.dangChay')
                : t(chayLai ? 'rpaAgent.thucThi.nutChayLai' : 'rpaAgent.thucThi.nutChay')}
            </Button>
          </div>
        </div>
      }
    >
      {xem.isPending ? (
        <div className="space-y-3 pt-2" aria-busy="true">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : xem.isError ? (
        <LoiRpa loi={xem.error} onThuLai={() => void xem.refetch()} />
      ) : (
        <div className="space-y-4 pt-2">
          <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <TruongThongTin nhan={t('rpaAgent.thucThi.hanhDong')}>
              <Badge variant={loai.variant}>{t(loai.khoa)}</Badge>
            </TruongThongTin>
            <TruongThongTin nhan={t('rpaAgent.thucThi.workflow')}>{xem.data.workflow?.name}</TruongThongTin>
            {xem.data.run && (
              <>
                <TruongThongTin nhan={t('rpaAgent.thucThi.luotLoi')}>
                  <span className="mr-2 break-all">{xem.data.run.name}</span>
                  <BadgeTrangThaiLuot status={xem.data.run.status} />
                </TruongThongTin>
                <TruongThongTin nhan={t('rpaAgent.thucThi.luotLoiLuc')}>
                  {ngayGioVN(xem.data.run.createdAt, locale)}
                  {xem.data.run.machineName ? ` · ${xem.data.run.machineName}` : ''}
                </TruongThongTin>
              </>
            )}
            <TruongThongTin nhan={t('rpaAgent.thucThi.robot')}>
              {xem.data.connected
                ? t('rpaAgent.thucThi.robotCuaBan', { ten: xem.data.robotName })
                : t('rpaAgent.thucThi.robotLicense')}
            </TruongThongTin>
          </dl>

          {hanhDong.reason && (
            <p className="rounded-lg bg-subtle p-3 text-sm text-content-secondary">
              <span className="font-medium text-content-primary">{t('rpaAgent.thucThi.lyDo')}: </span>
              {hanhDong.reason}
            </p>
          )}

          {projects.length === 0 ? (
            <LoiTrongHop>{t('rpaAgent.thucThi.khongCoProject')}</LoiTrongHop>
          ) : (
            <Field label={t('rpaAgent.thucThi.project')} required hint={t('rpaAgent.thucThi.projectGoiY')}>
              {(p) => (
                <Select<string>
                  id={p.id}
                  aria-describedby={p['aria-describedby']}
                  className="w-full"
                  showSearch
                  optionFilterProp="label"
                  value={projectId}
                  onChange={(v) => {
                    setDaChon(v);
                    thucThi.reset();
                  }}
                  placeholder={t('rpaAgent.thucThi.chonProject')}
                  options={projects.map((p) => ({ value: p.id, label: p.name ? `${p.name} (${p.id})` : p.id }))}
                />
              )}
            </Field>
          )}

          <p className="text-xs text-content-muted">{t(chayLai ? 'rpaAgent.thucThi.ghiChuChayLai' : 'rpaAgent.thucThi.ghiChuChay')}</p>
        </div>
      )}
    </Dialog>
  );
}
